import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IoNotificationsOutline, IoNotifications, IoClose, IoCheckmark } from 'react-icons/io5';
import { useNotifications } from '../contexts/NotificationContext';
import './NotificationBell.css';

const NotificationBell = () => {
  const { 
    notifications, 
    unreadCount, 
    loading, 
    markAsRead, 
    markAllAsRead,
    refreshNotifications 
  } = useNotifications();
  
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [anchor, setAnchor] = useState(null);

  // The dropdown renders into <body>: the header clips its own overflow, so a
  // dropdown inside it was cut off at the header's edge. Pin it under the bell.
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const place = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        setAnchor({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
      }
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside the bell and the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inBell = containerRef.current?.contains(event.target);
      const inDropdown = dropdownRef.current?.contains(event.target);
      if (!inBell && !inDropdown) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      refreshNotifications();
    }
  };

  const handleMarkAsRead = async (notificationId, event) => {
    event.stopPropagation();
    await markAsRead(notificationId, 'clicked');
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const getNotificationIcon = (type) => {
    const icons = {
      info: 'ℹ️',
      success: '✅',
      warning: '⚠️',
      error: '❌',
      announcement: '📢',
      reminder: '⏰',
      achievement: '🏆'
    };
    return icons[type] || '📢';
  };

  const getNotificationColor = (type) => {
    const colors = {
      info: 'var(--color-info)',
      success: 'var(--color-success)',
      warning: 'var(--color-warning)',
      error: 'var(--color-error)',
      announcement: 'var(--color-primary)',
      reminder: 'var(--color-secondary)',
      achievement: 'var(--color-orange)'
    };
    return colors[type] || 'var(--color-text-muted)';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const displayedNotifications = showAll ? notifications : notifications.slice(0, 5);
  const hasMoreNotifications = notifications.length > 5;

  return (
    <div className="notification-bell-container" ref={containerRef}>
      <button 
        className="notification-bell-button"
        onClick={toggleDropdown}
        aria-label="Notifications"
      >
        {unreadCount > 0 ? (
          <IoNotifications className="notification-icon" />
        ) : (
          <IoNotificationsOutline className="notification-icon" />
        )}
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && createPortal(
        <div
          className="notification-dropdown"
          ref={dropdownRef}
          style={anchor ? { '--bell-top': `${anchor.top}px`, '--bell-right': `${anchor.right}px` } : undefined}
        >
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-button"
                onClick={handleMarkAllAsRead}
              >
                Mark all read
              </button>
            )}
            <button 
              className="close-button"
              onClick={() => setIsOpen(false)}
            >
              <IoClose size={16} />
            </button>
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="notification-loading">Loading notifications...</div>
            ) : displayedNotifications.length === 0 ? (
              <div className="notification-empty">
                <IoNotificationsOutline size={32} />
                <p>No notifications</p>
              </div>
            ) : (
              displayedNotifications.map((notification) => (
                <div 
                  key={notification.id} 
                  className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                  onClick={() => handleMarkAsRead(notification.id, event)}
                >
                  <div className="notification-icon-container">
                    <span 
                      className="notification-type-icon"
                      style={{ color: getNotificationColor(notification.type) }}
                    >
                      {getNotificationIcon(notification.type)}
                    </span>
                  </div>
                  
                  <div className="notification-content">
                    <div className="notification-title">
                      {notification.title}
                    </div>
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-meta">
                      <span className="notification-time">
                        {formatTime(notification.createdAt)}
                      </span>
                      {notification.category && (
                        <span className="notification-category">
                          {notification.category}
                        </span>
                      )}
                    </div>
                  </div>

                  {!notification.isRead && (
                    <button 
                      className="mark-read-button"
                      onClick={(e) => handleMarkAsRead(notification.id, e)}
                      title="Mark as read"
                    >
                      <IoCheckmark size={14} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {hasMoreNotifications && (
            <div className="notification-footer">
              <button 
                className="show-more-button"
                onClick={() => setShowAll(!showAll)}
              >
                {showAll ? 'Show less' : `Show ${notifications.length - 5} more`}
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
